<template>
    <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.alerts") }}
        </h1>
    </div>

    <BasicTabs :tabs="tabs" :tab-key="'settings'" class="mt-[21px]" />

    <!-- Upgrade Required Section -->
    <div v-if="!hasAlertsAccess">
        <InfoCard
            :img-src="lockedNotAuthorized"
            img-alt="User has no access to content"
            :title="t('vue.upgrade_required')"
            :body-text="t('vue.not_having_access_message')"
            :show-dot="false"
            :disable-top-margin="false"
            :button-label="t('vue.upgrade_my_plan')"
            :button-href="route('billing.subscriptions.index')"
        />
    </div>

    <!-- Main Content -->
    <div v-else class="mb-[98px] mt-[42px]">
        <div class="grid-cols-layout pb-5 lg:grid">
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                >
                    {{ t("vue.compliance_settings") }}
                </h3>
                <p class="max-w-xs text-sm text-gray-500">
                    {{ t("vue.compliance_settings_alerts_explanation") }}
                </p>
            </div>

            <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h4 class="text-lg font-medium text-gray-900">
                                {{ t("vue.turn_off_on_alert_policy") }}
                            </h4>
                            <p
                                v-if="!enabledLocal"
                                class="mt-1 text-sm text-gray-600"
                            >
                                {{
                                    t(
                                        "vue.turn_off_on_alert_policy_explanation",
                                    )
                                }}
                            </p>
                        </div>

                        <label
                            class="inline-flex items-center cursor-pointer select-none"
                        >
                            <input
                                type="checkbox"
                                class="sr-only peer"
                                :checked="enabledLocal"
                                :disabled="enableLoading || disableLoading"
                                @change="onToggle"
                            />
                            <div
                                class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-red-600 peer-disabled:opacity-60 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"
                            ></div>
                        </label>
                    </div>

                    <div v-if="enabledLocal" class="mt-6 space-y-6 pb-[21px]">
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {{ t("vue.compliance_drop") }}
                            </label>
                            <div class="flex items-center space-x-3">
                                <NumberInput
                                    v-model="complianceDropNumber"
                                    :min="1"
                                    :max="100"
                                />
                                <span class="text-sm text-gray-500">%</span>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">
                                {{ t("vue.set_percent_drop_one_to_hundred") }}
                            </p>
                            <p class="mt-1 text-xs text-gray-500">
                                {{ t("vue.100_reports_min_to_report") }}
                            </p>
                        </div>

                        <div>
                            <div class="w-48">
                                <label
                                    class="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    {{ $t("vue.time_frame") }}
                                </label>
                                <SelectInput
                                    v-model="timeFrame"
                                    :data="timeFrameOptions"
                                >
                                    <template #help>
                                        {{
                                            t(
                                                "vue.define_time_period_to_monitor_compliance_drop",
                                            )
                                        }}
                                    </template>
                                </SelectInput>
                            </div>
                        </div>
                    </div>
                </div>

                <!--                :disabled="!enabledLocal || !hasChanges || loading"-->
                <div class="flex justify-end border-t border-gray-200">
                    <div class="my-3 pr-6">
                        <AtomicButton
                            type="button"
                            color="yellow"
                            size="sm"
                            @click="saveSettings"
                        >
                            {{
                                loading
                                    ? t("vue.saving_in_process")
                                    : t("vue.save_changes")
                            }}
                        </AtomicButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import NumberInput from "@/components/Atomic/Inputs/NumberInput.vue";
import SelectInput from "@/components/Atomic/Inputs/SelectInput.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import useAlertsAccess from "@/composables/useAlertsAccess";
import { Bell, Settings } from "lucide-vue-next";
import { ref, computed, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";

import type TabItem from "@/types/Settings/TabItem";
import type ISelectable from "@/types/Atomic/ISelectable";
import { useTranslations } from "@/composables/useTranslations.ts";
import useEnableAlerts from "@/composables/useEnableAlerts";

const { hasAlertsAccess } = useAlertsAccess();
const { t } = useTranslations();

const lockedNotAuthorized = new URL(
    "/resources/js/assets/images/locked-not-authorized.svg",
    import.meta.url,
).href;

interface Props {
    alert?: {
        id?: number;
        compliance_drop?: number;
        time_frame?: number;
        enabled?: boolean;
    };
}

const props = defineProps<Props>();

const timeFrameOptions: ISelectable[] = [
    { value: 86400, title: t("vue.time_frame_24h") },
    { value: 172800, title: t("vue.time_frame_48h") },
    { value: 604800, title: t("vue.time_frame_7d") },
];

const complianceDropNumber = ref<number>(props.alert?.compliance_drop || 25);
const timeFrame = ref<number>(props.alert?.time_frame || 86400);

const loading = ref<boolean>(false);
const enableLoading = ref<boolean>(false);
const disableLoading = ref<boolean>(false);

const originalComplianceDrop = ref<number>(props.alert?.compliance_drop || 25);
const originalTimeFrame = ref<number>(props.alert?.time_frame || 86400);

const hasChanges = computed(() => {
    return (
        complianceDropNumber.value !== originalComplianceDrop.value ||
        timeFrame.value !== originalTimeFrame.value
    );
});

const { enableAlerts } = useEnableAlerts();

const enabledLocal = ref<boolean>(props.alert?.enabled ?? false);

watch(
    () => props.alert?.enabled,
    (v) => {
        enabledLocal.value = !!v;
    },
    { immediate: true },
);

/**
 * Disable alerts: returns success flag so toggle can revert on failure.
 */
const disableAlerts = async (): Promise<boolean> => {
    if (disableLoading.value) return false;

    disableLoading.value = true;

    let ok = false;

    try {
        await router.post(
            route("alerts.disabled.store"),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    ok = true;
                    create({
                        title: "Alerts Disabled",
                        text: "Alerts have been successfully disabled.",
                        type: "success",
                    });
                },
                onError: () => {
                    ok = false;
                },
                onFinish: () => {
                    disableLoading.value = false;
                },
            },
        );
    } catch (error) {
        console.error("Error disabling alerts:", error);
        disableLoading.value = false;
        ok = false;
    }

    return ok;
};

/**
 * Switch handler: ON => enableAlerts(), OFF => disableAlerts()
 */
const onToggle = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const next = target.checked;

    // optimistic
    const prev = enabledLocal.value;
    enabledLocal.value = next;

    if (next) {
        if (enableLoading.value) {
            enabledLocal.value = prev;
            return;
        }

        enableLoading.value = true;
        try {
            await enableAlerts();

            create({
                title: "Alerts Enabled",
                text: "Alerts have been successfully enabled.",
                type: "success",
            });
        } catch (error) {
            console.error("Error enabling alerts:", error);
            enabledLocal.value = prev;
        } finally {
            enableLoading.value = false;
        }
        return;
    }

    if (disableLoading.value) {
        enabledLocal.value = prev;
        return;
    }
    const ok = await disableAlerts();
    if (!ok) enabledLocal.value = prev;
};

const saveSettings = async () => {
    if (!enabledLocal.value || !hasChanges.value || loading.value) return;

    loading.value = true;

    try {
        await router.put(
            route("alerts.settings.update"),
            {
                compliance_drop: complianceDropNumber.value,
                time_frame: timeFrame.value,
                enabled: true,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: "Alerts settings saved successfully",
                        text: "Alerts have been successfully saved.",
                        type: "success",
                    });
                },
            },
        );

        originalComplianceDrop.value = complianceDropNumber.value;
        originalTimeFrame.value = timeFrame.value;
    } catch (error) {
        console.error("Error saving alert settings:", error);
    } finally {
        loading.value = false;
    }
};

const tabs: TabItem[] = [
    {
        title: t("vue.overview"),
        key: "alerts/overview",
        icon: Bell,
        url: "/alerts/overview",
    },
    {
        title: t("vue.settings"),
        key: "settings",
        icon: Settings,
        url: "/alerts/settings",
    },
];
</script>
